import { Injectable } from '@angular/core';

export interface RoiInputs {
  teamSize: number;
  averageHourlyRate: number;
  projectsPerYear: number;
  averageLicenceCost: number;
  maturityLevel: number;  // 0-100 slider (Low to High maturity)
  productivityGainPercent?: number;
  reworkReductionPercent?: number;
  coordinationSavingsPercent?: number;
}

export interface WorkflowDistribution {
  coordinationPercent: number;
  modelingPercent: number;
  reworkPercent: number;
  coordinationHours: number;
  modelingHours: number;
  reworkHours: number;
}

export interface ImprovementPotential {
  coordinationImprovement: number;
  modelingImprovement: number;
  reworkImprovement: number;
}

export interface RoiResults {
  estimatedAnnualSavings: number;
  productivityGain: number;
  paybackPeriodMonths: number;
  extraProjectCapacity: number;
  productionGainPercent: number;
  reworkReductionPercent: number;
  coordinationSavingsPercent: number;
  currentDistribution: WorkflowDistribution;
}

@Injectable({
  providedIn: 'root'
})
export class RoiCalculatorService {
  // Reference implementation constants
  private readonly EFFECTIVE_HOURS_PER_WEEK = 36;
  private readonly WEEKS_PER_YEAR = 48;
  private readonly HOURS_PER_PERSON_PER_YEAR = this.EFFECTIVE_HOURS_PER_WEEK * this.WEEKS_PER_YEAR; // 1728

  // Low maturity workflow distribution (maturity = 0)
  private readonly LOW_MATURITY = {
    coordination: 20,
    modeling: 45,
    rework: 35
  };

  // High maturity workflow distribution (maturity = 100)
  private readonly HIGH_MATURITY = {
    coordination: 15,
    modeling: 70,
    rework: 15
  };

  private roundTo(value: number, decimals = 1): number {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }

  /**
   * Calculate workflow distribution based on maturity level
   * Interpolates between low and high maturity
   */
  getWorkflowDistribution(maturityLevel: number): WorkflowDistribution {
    const t = maturityLevel / 100;
    
    const coordinationPercent = this.LOW_MATURITY.coordination + 
      (this.HIGH_MATURITY.coordination - this.LOW_MATURITY.coordination) * t;
    const modelingPercent = this.LOW_MATURITY.modeling + 
      (this.HIGH_MATURITY.modeling - this.LOW_MATURITY.modeling) * t;
    const reworkPercent = this.LOW_MATURITY.rework + 
      (this.HIGH_MATURITY.rework - this.LOW_MATURITY.rework) * t;

    return {
      coordinationPercent,
      modelingPercent,
      reworkPercent,
      coordinationHours: this.EFFECTIVE_HOURS_PER_WEEK * coordinationPercent / 100,
      modelingHours: this.EFFECTIVE_HOURS_PER_WEEK * modelingPercent / 100,
      reworkHours: this.EFFECTIVE_HOURS_PER_WEEK * reworkPercent / 100
    };
  }

  /**
   * Calculate improvement potential from current to target
   */
  getImprovementPotential(current: WorkflowDistribution): ImprovementPotential {
    // Improvement assumptions for value analysis scenario
    const targetEfficiency = {
      coordination: 10,
      modeling: 80,
      rework: 10
    };

    const coordinationImprovement = current.coordinationPercent > 0 
      ? ((current.coordinationPercent - targetEfficiency.coordination) / current.coordinationPercent * 100)
      : 0;
    const reworkImprovement = current.reworkPercent > 0
      ? ((current.reworkPercent - targetEfficiency.rework) / current.reworkPercent * 100)
      : 0;
    const modelingImprovement = current.modelingPercent > 0
      ? ((targetEfficiency.modeling - current.modelingPercent) / current.modelingPercent * 100)
      : 0;

    return {
      coordinationImprovement: Math.max(0, coordinationImprovement),
      modelingImprovement: Math.max(0, modelingImprovement),
      reworkImprovement: Math.max(0, reworkImprovement)
    };
  }

  calculateRoi(inputs: RoiInputs): RoiResults {
    const {
      teamSize,
      averageHourlyRate,
      projectsPerYear,
      averageLicenceCost,
      maturityLevel,
      productivityGainPercent,
      reworkReductionPercent,
      coordinationSavingsPercent
    } = inputs;

    const currentDistribution = this.getWorkflowDistribution(maturityLevel);
    const defaultImprovements = this.getImprovementPotential(currentDistribution);
    const productionGain = this.clampPercent(productivityGainPercent ?? defaultImprovements.modelingImprovement);
    const reworkReduction = this.clampPercent(reworkReductionPercent ?? defaultImprovements.reworkImprovement);
    const coordinationSavings = this.clampPercent(coordinationSavingsPercent ?? defaultImprovements.coordinationImprovement);

    // Weighted production gain using distribution percentages (independent of teamSize)
    const productionGainRaw =
      (currentDistribution.modelingPercent * productionGain +
       currentDistribution.reworkPercent * reworkReduction +
       currentDistribution.coordinationPercent * coordinationSavings) / 100;

    const estimatedAnnualSavings = teamSize * averageHourlyRate * this.HOURS_PER_PERSON_PER_YEAR * productionGainRaw / 100;
    const productivityGain = Math.round(productionGainRaw * 10) / 10;
    const extraProjectCapacity = Math.round(projectsPerYear * productionGainRaw / 100);

    const totalLicenseCost = teamSize * averageLicenceCost;
    const paybackPeriodMonths = estimatedAnnualSavings > 0
      ? (totalLicenseCost / estimatedAnnualSavings) * 12
      : 0;

    return {
      estimatedAnnualSavings,
      productivityGain,
      paybackPeriodMonths,
      extraProjectCapacity,
      productionGainPercent: productionGain,
      reworkReductionPercent: reworkReduction,
      coordinationSavingsPercent: coordinationSavings,
      currentDistribution,
    };
  }

  private clampPercent(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }

    return Math.max(0, Math.min(100, value));
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  formatDecimal(value: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  }
}
